import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef, useMemo } from 'react'
import { notificationsApi } from '../../services/api'

interface AppNotification {
  id: string
  subject: string
  message: string
  targetType: 'ALL_ADMINS' | 'ORGANIZATION_ADMINS' | 'SPECIFIC_USERS'
  targetEmails: string[]
  organizationIds: string[]
  sentBy: string
  sentAt: string
  isRead?: boolean
}

interface NotificationContextType {
  notifications: AppNotification[]
  unreadCount: number
  loading: boolean
  loadNotifications: () => Promise<void>
  markAsRead: (id: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  refreshUnreadCount: () => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

interface NotificationProviderProps {
  children: ReactNode
  user: any
}

export function NotificationProvider({ children, user }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [loading, setLoading] = useState(false)
  const isLoadingRef = useRef(false)
  const lastUserIdRef = useRef<string | null>(null)

  // Memoize the current user ID to prevent unnecessary re-renders
  const currentUserId = useMemo(() => user?.id, [user?.id])

  const loadNotifications = useCallback(async () => {
    // Only load notifications if user is authenticated
    if (!user) {
      setNotifications([])
      return
    }

    // Prevent multiple simultaneous requests
    if (isLoadingRef.current) {
      return
    }

    try {
      setLoading(true)
      isLoadingRef.current = true
      
      console.log('🔔 Loading notifications for user:', user.id)
      const response = await notificationsApi.getAll()
      console.log('🔔 Notifications response:', response)
      
      // Check if response is valid
      if (!response) {
        setNotifications([])
        return
      }
      
      const notificationData = response.data || []
      console.log('🔔 Processed notification data:', notificationData)
      setNotifications(notificationData as AppNotification[])
      
    } catch (error: any) {
      console.error('🔔 Error loading notifications:', error)
      console.error('🔔 Error details:', error.response?.data)
      console.error('🔔 Error status:', error.response?.status)
      // Set empty array on error to prevent crashes
      setNotifications([])
    } finally {
      setLoading(false)
      isLoadingRef.current = false
    }
  }, [user])

  const markAsRead = async (id: string) => {
    if (!user) return
    
    try {
      console.log('🔔 Marking notification as read:', id)
      const result = await notificationsApi.markAsRead(id)
      console.log('🔔 Mark as read result:', result)
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === id 
            ? { ...notif, isRead: true }
            : notif
        )
      )
      console.log('🔔 Updated local notifications state for notification:', id)
    } catch (error: any) {
      console.error('🔔 Error marking notification as read:', error)
      console.error('🔔 Error details:', error.response?.data)
      console.error('🔔 Error status:', error.response?.status)
      throw error // Re-throw to let the component handle it
    }
  }

  const markAllAsRead = async () => {
    if (!user) return
    
    try {
      console.log('🔔 Marking all notifications as read for user:', user.id)
      const result = await notificationsApi.markAllAsRead()
      console.log('🔔 Mark all as read result:', result)
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, isRead: true }))
      )
      
      console.log('🔔 Updated local notifications state')
    } catch (error: any) {
      console.error('🔔 Error marking all notifications as read:', error)
      console.error('🔔 Error details:', error.response?.data)
      console.error('🔔 Error status:', error.response?.status)
      throw error // Re-throw to let the component handle it
    }
  }

  const refreshUnreadCount = useCallback(() => {
    if (user && !isLoadingRef.current) {
      loadNotifications()
    }
  }, [user, loadNotifications])

  const unreadCount = useMemo(() => {
    return notifications.filter((n: AppNotification) => !n.isRead).length
  }, [notifications])

  useEffect(() => {
    // Only load notifications if user exists, user ID changed, and we're not already loading
    if (currentUserId && currentUserId !== lastUserIdRef.current && !isLoadingRef.current) {
      lastUserIdRef.current = currentUserId
      loadNotifications()
    } else if (!currentUserId) {
      // Reset when user logs out
      lastUserIdRef.current = null
      setNotifications([])
    }
  }, [currentUserId, loadNotifications])

  // Auto-refresh notifications when a socket push arrives
  useEffect(() => {
    const handleNewNotification = () => {
      if (!isLoadingRef.current) {
        loadNotifications()
      }
    }
    window.addEventListener('notification:new', handleNewNotification as EventListener)
    return () => {
      window.removeEventListener('notification:new', handleNewNotification as EventListener)
    }
  }, [loadNotifications])

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    refreshUnreadCount
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
} 